/**
 * <recently-viewed-section>
 *
 * Reads handles from @theme/recently-viewed, fetches product cards via
 * /products/{handle}?section_id=helper-recently-viewed-card.
 *
 * Markup: sections/section-recently_viewed.liquid
 */

import { responsive } from '@theme/responsive';
import { getRecentlyViewedHandles, RECENTLY_VIEWED_CHANGED_EVENT } from '@theme/recently-viewed';

class RecentlyViewedSection extends HTMLElement {
  #initialized = false;
  #abortController = null;
  #revealObserver = null;
  #revealFrame = null;
  #onChanged = null;
  #onBreakpointChange = null;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.hidden = true;

    this.#onChanged = () => {
      void this.#render();
    };

    this.#onBreakpointChange = (event) => {
      const layout = this.querySelector('[data-recently-viewed-layout]');
      if (!layout) return;
      this.#syncDesktopSwiper(layout, event.detail.matches);
    };

    responsive.addBreakpointListener('lap_and_up');
    document.body.addEventListener('breakpoint:lap_and_up', this.#onBreakpointChange);
    document.addEventListener(RECENTLY_VIEWED_CHANGED_EVENT, this.#onChanged);
    void this.#render();
  }

  disconnectedCallback() {
    this.#initialized = false;
    this.#abortController?.abort();
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    if (this.#revealFrame) cancelAnimationFrame(this.#revealFrame);
    this.#revealFrame = null;
    document.body.removeEventListener('breakpoint:lap_and_up', this.#onBreakpointChange);
    document.removeEventListener(RECENTLY_VIEWED_CHANGED_EVENT, this.#onChanged);
  }

  #cloneTemplate(id) {
    const template = this.querySelector(`#${id}`);
    return template?.content.firstElementChild?.cloneNode(true) || null;
  }

  #cancelReveal() {
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    if (this.#revealFrame) cancelAnimationFrame(this.#revealFrame);
    this.#revealFrame = null;
  }

  async #fetchCard(handle, signal) {
    const localeRoot = window.routes?.localeRootUrl || '';
    const url = `${localeRoot}/products/${encodeURIComponent(handle)}?section_id=helper-recently-viewed-card`;

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return null;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return doc.querySelector('[data-recently-viewed-card]');
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return null;
    }
  }

  #getMaxProducts() {
    const value = Number(this.dataset.maxProducts);
    return Number.isFinite(value) && value > 0 ? value : 8;
  }

  #getHandles() {
    const maxProducts = this.#getMaxProducts();
    const currentHandle = this.dataset.currentHandle;

    return getRecentlyViewedHandles()
      .filter(Boolean)
      .filter((handle) => !currentHandle || handle !== currentHandle)
      .slice(0, maxProducts);
  }

  #prepareSlide(slide, { asSwiperSlide = false } = {}) {
    slide.classList.toggle('swiper-slide', asSwiperSlide);
    slide.classList.remove('animatedContent');
    return slide;
  }

  #buildLayout(slides, count) {
    const layout = this.#cloneTemplate('recently-viewed-layout-template');
    const grid = layout?.querySelector('[data-recently-viewed-grid]');
    if (!layout || !grid) return null;

    if (count > 4) {
      layout.classList.add('recently-viewed__layout--swiper-desktop');
    }

    grid.classList.add(`recently-viewed__grid--count-${count}`);

    slides.forEach((slide) => {
      grid.appendChild(this.#prepareSlide(slide));
    });

    return layout;
  }

  #syncDesktopSwiper(layout, isDesktop = responsive.matchesBreakpoint('lap_and_up')) {
    const swiperMount = layout.querySelector('[data-recently-viewed-swiper-mount]');
    const grid = layout.querySelector('[data-recently-viewed-grid]');
    if (!swiperMount || !grid) return;

    const useSwiper = layout.classList.contains('recently-viewed__layout--swiper-desktop');

    if (!useSwiper || !isDesktop) {
      swiperMount.replaceChildren();
      return;
    }

    if (swiperMount.hasChildNodes()) return;

    const swiperBlock = this.#cloneTemplate('recently-viewed-swiper-template');
    const wrapper = swiperBlock?.querySelector('[data-recently-viewed-swiper-wrapper]');
    if (!swiperBlock || !wrapper) return;

    [...grid.children].forEach((slide) => {
      wrapper.appendChild(this.#prepareSlide(slide.cloneNode(true), { asSwiperSlide: true }));
    });

    swiperMount.appendChild(swiperBlock);
  }

  #revealLayout(layout) {
    const animated = layout.querySelector('[data-recently-viewed-animated]');
    if (!animated) return;

    this.#cancelReveal();

    const reveal = () => {
      this.#revealFrame = requestAnimationFrame(() => {
        animated.classList.add('recently-viewed__animated--visible');
        this.#revealFrame = null;
      });
    };

    if (!('IntersectionObserver' in window)) {
      reveal();
      return;
    }

    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        const shouldReveal = entries.some(
          (entry) =>
            entry.isIntersecting || entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0)
        );
        if (!shouldReveal) return;

        this.#revealObserver?.disconnect();
        this.#revealObserver = null;
        reveal();
      },
      {
        rootMargin: '0px 0px 8% 0px',
        threshold: 0.01,
      }
    );
    this.#revealObserver.observe(animated);
  }

  async #render() {
    const content = this.querySelector('[data-recently-viewed-content]');
    const handles = this.#getHandles();

    if (!handles.length) {
      this.#cancelReveal();
      this.hidden = true;
      content?.replaceChildren();
      return;
    }

    const hasRenderedLayout = Boolean(content?.querySelector('[data-recently-viewed-layout]'));
    if (!hasRenderedLayout) this.hidden = true;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const slides = (
        await Promise.all(handles.map((handle) => this.#fetchCard(handle, signal)))
      ).filter(Boolean);

      if (signal.aborted) return;

      if (!slides.length) {
        this.hidden = true;
        content?.replaceChildren();
        return;
      }

      const layout = this.#buildLayout(slides, slides.length);
      if (!layout) {
        this.hidden = true;
        content?.replaceChildren();
        return;
      }

      content?.replaceChildren(layout);
      this.#syncDesktopSwiper(layout);
      this.hidden = false;
      this.#revealLayout(layout);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (!hasRenderedLayout) {
        this.hidden = true;
        content?.replaceChildren();
      }
    }
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
